import * as React from "react";
import { Button } from "@/components/ui/button";
import { parseFormation } from "@/lib/formation-parser";
import {
  placePlayers,
  getPlayerAt,
  type Player as FormationPlayer,
  type PlacedPlayer,
} from "@/lib/formation-placement";

type Props = {
  formationHome?: string;
  formationAway?: string;
  playersHome?: FormationPlayer[];
  playersAway?: FormationPlayer[];
  onPlayerClick?: (p: PlacedPlayer) => void;
};

function splitGoalkeeper(players: FormationPlayer[]) {
  let gk: FormationPlayer | undefined;
  const outfield = players.filter((p) => {
    const raw = (p.positionRaw || "").toUpperCase();
    const isGk = raw.includes("GOALKEEPER") || raw === "GK";
    if (isGk && !gk) {
      gk = p;
      return false;
    }
    return true;
  });
  return { gk, outfield };
}

const FormationGrid: React.FC<Props> = ({
  formationHome = "",
  formationAway = "",
  playersHome = [],
  playersAway = [],
  onPlayerClick,
}) => {
  const rowsHomeBase = React.useMemo(() => parseFormation(formationHome), [formationHome]);
  const rowsAwayBase = React.useMemo(() => parseFormation(formationAway), [formationAway]);

  const rowsHomeVisual = React.useMemo(() => rowsHomeBase.slice().reverse(), [rowsHomeBase]);
  const rowsAwayVisual = rowsAwayBase;

  const { gk: gkHome, outfield: ofHome } = React.useMemo(() => splitGoalkeeper(playersHome), [playersHome]);
  const { gk: gkAway, outfield: ofAway } = React.useMemo(() => splitGoalkeeper(playersAway), [playersAway]);

  const placedHome = React.useMemo(
    () => placePlayers(ofHome, formationHome ?? "", false),
    [ofHome, formationHome]
  );
  const placedAway = React.useMemo(
    () => placePlayers(ofAway, formationAway ?? "", true),
    [ofAway, formationAway]
  );

  const renderOutfieldRow = (
    side: "home" | "away",
    count: number,
    visualRowIdx: number
  ) => {
    const logicalRowIdx =
      side === "home"
      ? (rowsHomeVisual.length - 1 - visualRowIdx)
      : (rowsAwayVisual.length - 1 - visualRowIdx);

    const find = side === "home"
      ? (c: number) => getPlayerAt(placedHome, logicalRowIdx, c)
      : (c: number) => getPlayerAt(placedAway, logicalRowIdx, c);

    return (
      <div>
        <div
          key={`${side}-row-${visualRowIdx}`}
          className="grid"
          style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`, gap: 10 }}
        >
          {Array.from({ length: count }).map((_, colIdx) => {
            const p = find(colIdx);
            const number = p?.shirtNo ?? p?.shirtNo === 0 ? p.shirtNo : undefined;
            return (
              <Button
                key={`${side}-${visualRowIdx}-${colIdx}`}
                className="w-10 h-10 rounded-full shadow-sm text-sm font-medium mx-auto flex items-center justify-center"
                variant={p ? "default" : "secondary"}
                onClick={() => p && onPlayerClick?.(p)}
                title={p ? `${p.name}` : undefined}
                aria-label={p ? `${p.name}` : "Vacant slot"}
              >
                {p ? (Number.isFinite(number) ? number : (colIdx + 1)) : (colIdx + 1)}
              </Button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderGkRow = (side: "home" | "away") => {
    const gk = side === "home" ? gkHome : gkAway;
    return (
      <div
        key={`${side}-gk`}
        className="grid"
        style={{ gridTemplateColumns: "repeat(1, minmax(0, 1fr))", gap: 10 }}
      >
        <Button
          className="w-10 h-10 rounded-full ring-2 ring-white shadow-sm text-sm font-medium mx-auto flex items-center justify-center"
          variant={gk ? "default" : "secondary"}
          onClick={() => gk && onPlayerClick?.({ ...gk, row: -1, col: 0 })}
          title={gk ? `${gk.name}` : undefined}
          aria-label={gk ? `${gk.name}` : "Goalkeeper"}
        >
          {gk?.shirtNo ?? 1}
        </Button>
      </div>
    );
  };

  return (
  <div className="relative max-w-3xl w-[500px] max-h-[800px]">
    <img
      src="/img/pitch/temp-football-pitch.png"
      alt="Pitch"
      className="w-[500px] h-[800px] rounded-xl shadow border border-border"
    />
    <div className="absolute h-[800px] inset-0 p-6">
      <div className="h-1/2 flex flex-col pb-1 justify-between pointer-events-none">
        <div className="pointer-events-auto">{renderGkRow("away")}</div>
        {rowsAwayVisual.map((count, i) => (
          <div key={`away-wrap-${i}`} className="pointer-events-auto">
            {renderOutfieldRow("away", count, i)}
          </div>
        ))}
      </div>

      <div className="h-1/2 flex flex-col pt-2 justify-between pointer-events-none">
        {rowsHomeVisual.map((count, i) => (
          <div key={`home-wrap-${i}`} className="pointer-events-auto">
            {renderOutfieldRow("home", count, i)}
          </div>
        ))}
        <div className="pointer-events-auto">{renderGkRow("home")}</div>
      </div>
    </div>
  </div>
  );
};

export default FormationGrid;
